// Haptic feedback utility for mobile devices
// Uses the Vibration API (supported on Android, some iOS browsers)

// Check if vibration is supported
export const isHapticSupported = () => {
  return typeof navigator !== 'undefined' && 'vibrate' in navigator;
};

// Vibration patterns (in milliseconds)
const PATTERNS = {
  // Light tap - for selection/swipe start
  tap: 10,
  
  // Short buzz - for invalid moves
  error: [30, 50, 30],
  
  // Medium pulse - for successful match
  match: 25,
  
  // Double pulse - for special fruit created (4-match)
  special: [20, 40, 30],
  
  // Triple pulse - for disco/big combo (5+ match)
  combo: [15, 30, 15, 30, 25],
  
  // Strong pulse - for explosion/row clear
  explosion: [40, 20, 40],
  
  // Celebration pattern - for level complete
  win: [30, 50, 30, 50, 50, 100, 100],
  
  // Sad pattern - for level fail
  lose: [100, 50, 100],
  
  // Level up celebration
  levelUp: [50, 30, 50, 30, 100, 50, 150],
  
  // Prestige achievement
  prestige: [100, 50, 100, 50, 100, 100, 200, 100, 300],
};

// Trigger haptic feedback
export const haptic = {
  // Light tap feedback
  tap: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.tap);
    }
  },
  
  // Error/invalid move feedback
  error: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.error);
    }
  },
  
  // Successful match feedback
  match: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.match);
    }
  },
  
  // Special fruit created (striped)
  special: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.special);
    }
  },
  
  // Big combo (disco melon, 5+ match)
  combo: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.combo);
    }
  },
  
  // Explosion/row clear
  explosion: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.explosion);
    }
  },
  
  // Level complete
  win: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.win);
    }
  },
  
  // Level failed
  lose: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.lose);
    }
  },
  
  // Level up
  levelUp: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.levelUp);
    }
  },
  
  // Prestige achievement
  prestige: () => {
    if (isHapticSupported()) {
      navigator.vibrate(PATTERNS.prestige);
    }
  },
  
  // Custom pattern
  custom: (pattern) => {
    if (isHapticSupported()) {
      navigator.vibrate(pattern);
    }
  },
  
  // Stop any ongoing vibration
  stop: () => {
    if (isHapticSupported()) {
      navigator.vibrate(0);
    }
  },
};

// React hook for haptic feedback
import { useCallback, useState, useEffect } from 'react';

export function useHaptic() {
  const [enabled, setEnabled] = useState(() => {
    const saved = localStorage.getItem('fruitCrush_hapticEnabled');
    // Default to true if supported, false if not
    return saved !== null ? saved === 'true' : isHapticSupported();
  });
  
  // Save preference to localStorage
  useEffect(() => {
    localStorage.setItem('fruitCrush_hapticEnabled', enabled.toString());
  }, [enabled]);
  
  // Toggle haptic on/off
  const toggleHaptic = useCallback(() => {
    setEnabled(prev => !prev);
  }, []);
  
  // Wrapped haptic functions that respect enabled state
  const tap = useCallback(() => {
    if (enabled) haptic.tap();
  }, [enabled]);
  
  const error = useCallback(() => {
    if (enabled) haptic.error();
  }, [enabled]);
  
  const match = useCallback(() => {
    if (enabled) haptic.match();
  }, [enabled]);
  
  const special = useCallback(() => {
    if (enabled) haptic.special();
  }, [enabled]);
  
  const combo = useCallback(() => {
    if (enabled) haptic.combo();
  }, [enabled]);
  
  const explosion = useCallback(() => {
    if (enabled) haptic.explosion();
  }, [enabled]);
  
  const win = useCallback(() => {
    if (enabled) haptic.win();
  }, [enabled]);
  
  const lose = useCallback(() => {
    if (enabled) haptic.lose();
  }, [enabled]);
  
  const levelUp = useCallback(() => {
    if (enabled) haptic.levelUp();
  }, [enabled]);
  
  const prestige = useCallback(() => {
    if (enabled) haptic.prestige();
  }, [enabled]);
  
  return {
    enabled,
    supported: isHapticSupported(),
    toggleHaptic,
    setEnabled,
    tap,
    error,
    match,
    special,
    combo,
    explosion,
    win,
    lose,
    levelUp,
    prestige,
  };
}
